import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BACKEND_BASE_URL } from "./api";

const AUTH = `${BACKEND_BASE_URL}/api/auth`;
const TOKEN_KEY = "hypes_psi_token";

// Attach Authorization header globally if token exists
function applyAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Apply token to axios immediately on mount
  useEffect(() => {
    applyAxiosToken(token);
  }, [token]);

  // Validate token on mount / when it changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setUser(null); setLoading(false); return; }
      try {
        const res = await axios.get(`${AUTH}/me`);
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) {
          try { localStorage.removeItem(TOKEN_KEY); } catch {}
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${AUTH}/login`, { email, password });
    const { token: t, user: u } = res.data;
    try { localStorage.setItem(TOKEN_KEY, t); } catch {}
    applyAxiosToken(t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await axios.post(`${AUTH}/register`, { email, password, name });
    const { token: t, user: u } = res.data;
    try { localStorage.setItem(TOKEN_KEY, t); } catch {}
    applyAxiosToken(t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    applyAxiosToken(null);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
